import { useEffect, useRef, useState } from "react";
import { Paperclip, Mic, SendHorizontal, Image as ImageIcon, Camera, FileText, Receipt, X, Square, Trash2, Music } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useRecorder } from "./useRecorder";
import { fmtSize } from "@/lib/format";
import { AUDIO_ACCEPT, AUDIO_EXT } from "@/lib/library";

const IMG = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/gif"];
const DOCS = ["application/pdf", "application/msword", "text/plain", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];
const PICKERS = {
  camera: { kind: "image", accept: "image/*", capture: "environment", label: "Câmera", icon: Camera, types: IMG },
  image: { kind: "image", accept: "image/*", multiple: true, label: "Galeria de fotos", icon: ImageIcon, types: IMG },
  document: { kind: "document", accept: ".pdf,.doc,.docx,.txt,.xls,.xlsx", label: "Documento (PDF, Word, Excel)", icon: FileText, types: DOCS },
  audio: { kind: "audio", accept: AUDIO_ACCEPT, label: "Arquivo de áudio", icon: Music },
  receipt: { kind: "receipt", accept: "image/*,application/pdf", label: "Comprovante Pix", icon: Receipt, types: [...IMG, "application/pdf"] },
};
const KIND_LABEL = { image: "Fotografia", document: "Documento", receipt: "Comprovante Pix" };
const MAX = 16 * 1024 * 1024;
const MAX_ITEMS = 10;
const DRAFT_KEY = "zapiudp_draft";

const Thumb = ({ it, onRemove, onView }) => (
  <div data-testid="composer-attachment" className="relative shrink-0">
    {it.preview ? (
      <button type="button" data-testid="composer-attachment-view" onClick={() => onView(it)} className="block h-16 w-16 overflow-hidden rounded-md ring-1 ring-zap-line">
        <img src={it.preview} alt="Prévia" className="h-full w-full object-cover" />
      </button>
    ) : (
      <div className="flex h-16 w-44 items-center gap-2 rounded-md bg-white px-2 ring-1 ring-zap-line">
        <FileText className="h-7 w-7 shrink-0 text-zap-deep" />
        <div className="min-w-0"><p className="text-xs font-semibold text-zap-gold">{KIND_LABEL[it.kind]}</p><p className="truncate text-[13px]">{it.file.name}</p><p className="text-xs opacity-60">{fmtSize(it.file.size)}</p></div>
      </div>
    )}
    <button data-testid="composer-attachment-remove" onClick={() => onRemove(it.id)} aria-label="Remover anexo"
      className="absolute -right-3 -top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-zap-ink text-white"><X className="h-4 w-4" /></span></button>
  </div>
);

const Attachments = ({ items, onRemove }) => {
  const [view, setView] = useState(null);
  if (items[0]?.kind === "audio") {
    return (
      <div data-testid="composer-attachment" className="flex items-center gap-3 border-t border-zap-line bg-zap-mist px-3 py-2">
        <audio data-testid="composer-audio-preview" controls src={items[0].preview} className="h-11 min-w-0 flex-1" />
        <button data-testid="composer-attachment-remove" onClick={() => onRemove(items[0].id)} aria-label="Descartar áudio" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-black/5"><Trash2 className="h-5 w-5 text-red-700" /></button>
      </div>
    );
  }
  return (
    <>
      <div className="flex gap-5 overflow-x-auto border-t border-zap-line bg-zap-mist px-3 pb-2 pr-5 pt-4">
        {items.map((it) => <Thumb key={it.id} it={it} onRemove={onRemove} onView={setView} />)}
      </div>
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent data-testid="composer-image-viewer" className="max-w-2xl p-2">
          <DialogTitle className="sr-only">Visualizar fotografia</DialogTitle>
          {view && <img src={view.preview} alt="Fotografia selecionada" className="max-h-[80vh] w-full rounded object-contain" />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const Composer = ({ onSend }) => {
  const [text, setText] = useState(() => localStorage.getItem(DRAFT_KEY) || "");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    text ? localStorage.setItem(DRAFT_KEY, text) : localStorage.removeItem(DRAFT_KEY);
  }, [text]);
  const input = useRef(null);
  const grow = useRef(null);
  useEffect(() => {
    const el = grow.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight + 2, 168)}px`;
    el.style.overflowY = el.scrollHeight > 166 ? "auto" : "hidden";
  }, [text]);
  const pickerRef = useRef("image");
  const recorder = useRecorder();
  const audioMode = items[0]?.kind === "audio";

  const pick = (key) => {
    const p = PICKERS[key];
    pickerRef.current = key;
    input.current.accept = p.accept;
    input.current.multiple = !!p.multiple;
    p.capture ? input.current.setAttribute("capture", p.capture) : input.current.removeAttribute("capture");
    input.current.click();
  };
  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const p = PICKERS[pickerRef.current];
    if (p.kind === "audio") {
      const file = files[0];
      if (!file) return;
      const ext = file.name.split(".").pop().toLowerCase();
      if (!(file.type || "").startsWith("audio/") && !AUDIO_EXT.includes(ext)) { toast.error(`Formato não aceito: ${file.name}`); return; }
      if (file.size > MAX) { toast.error(`Arquivo muito grande (limite 16 MB): ${file.name}`); return; }
      setItems([{ id: `a-${Date.now()}`, kind: "audio", file, preview: URL.createObjectURL(file) }]);
      return;
    }
    const base = audioMode ? [] : items;
    const accepted = [];
    for (const file of files) {
      const type = (file.type || "").toLowerCase();
      if (!p.types.includes(type)) { toast.error(`Formato não aceito: ${file.name}`); continue; }
      if (file.size > MAX) { toast.error(`Arquivo muito grande (limite 16 MB): ${file.name}`); continue; }
      if (base.length + accepted.length >= MAX_ITEMS) { toast.error(`Até ${MAX_ITEMS} anexos por envio.`); break; }
      accepted.push({ id: `${Date.now()}-${Math.random()}`, kind: p.kind, file, preview: type.startsWith("image/") && !type.includes("hei") ? URL.createObjectURL(file) : null });
    }
    if (accepted.length) setItems([...base, ...accepted]);
  };
  const startRec = async () => {
    try {
      await recorder.start();
    } catch {
      toast.error("Permita o uso do microfone para gravar áudio.");
    }
  };
  const stopRec = async () => {
    const blob = await recorder.stop();
    if (!blob?.size) return;
    const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
    const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: blob.type });
    setItems([{ id: `a-${Date.now()}`, kind: "audio", file, preview: URL.createObjectURL(file) }]);
  };
  const send = async () => {
    if (busy || (!text.trim() && !items.length)) return;
    setBusy(true);
    const drafts = items.length
      ? items.map((it, i) => ({ kind: it.kind, file: it.file, preview: it.preview, text: i === 0 ? text.trim() : "" }))
      : [{ kind: "text", text: text.trim() }];
    const ok = await onSend(drafts);
    setBusy(false);
    if (ok) {
      setText("");
      setItems([]);
    }
  };
  const iconBtn = "flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-zap-deep transition-colors hover:bg-black/5";
  const mm = `${String(Math.floor(recorder.seconds / 60)).padStart(2, "0")}:${String(recorder.seconds % 60).padStart(2, "0")}`;

  return (
    <div className="bg-white">
      {items.length > 0 && <Attachments items={items} onRemove={(id) => setItems(items.filter((x) => x.id !== id))} />}
      <input ref={input} type="file" className="hidden" data-testid="composer-file-input" onChange={onFile} />
      {recorder.recording ? (
        <div data-testid="recording-bar" className="flex items-center gap-2 px-2 py-2">
          <button data-testid="recording-cancel" onClick={recorder.cancel} aria-label="Descartar gravação" className={iconBtn}><Trash2 className="h-6 w-6 text-red-700" /></button>
          <div className="flex flex-1 items-center gap-2 text-[17px] font-semibold"><span className="rec-dot h-3 w-3 rounded-full bg-red-600" /> Gravando {mm}</div>
          <button data-testid="recording-stop" onClick={stopRec} className="flex h-12 items-center gap-2 rounded-full bg-zap-deep px-5 font-bold text-white"><Square className="h-4 w-4 fill-white" /> CONCLUIR</button>
        </div>
      ) : (
        <div className="flex items-end gap-1 px-2 py-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="attach-button" aria-label="Anexar" className={iconBtn}><Paperclip className="h-6 w-6" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-72">
              {Object.entries(PICKERS).map(([k, p]) => (
                <DropdownMenuItem key={k} data-testid={`attach-${k}`} onSelect={() => pick(k)} className="h-12 gap-3 text-base">
                  <p.icon className="h-5 w-5 text-zap-deep" /> {p.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <textarea ref={grow} data-testid="message-input" value={text} rows={1} maxLength={4000}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.innerWidth > 768) { e.preventDefault(); send(); } }}
            placeholder={items.length ? "Legenda (opcional)" : "Mensagem"}
            className="max-h-[168px] min-h-12 flex-1 resize-none rounded-3xl border border-zap-line bg-zap-mist px-4 py-3 text-[17px] leading-snug outline-none placeholder:text-zap-ink/45 focus:border-zap-green" />
          <button data-testid="mic-button" onClick={startRec} disabled={items.length > 0} aria-label="Gravar áudio" className={`${iconBtn} disabled:opacity-30`}><Mic className="h-6 w-6" /></button>
          <button data-testid="send-button" onClick={send} disabled={busy || (!text.trim() && !items.length)} aria-label="Enviar"
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-zap-deep text-white transition-colors hover:bg-zap-ink disabled:bg-zap-deep/40">
            <SendHorizontal className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};
